import React from "react";
import { Link as RouterLink, Navigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import {
  LoginOutlined,
  Nfc as NfcIcon,
  PointOfSale,
  Storefront,
} from "@mui/icons-material";
import { BRAND } from "../../constants/brand";
import PoweredByBrand from "../../components/brand/PoweredByBrand";
import { useAuthStore } from "../../stores/authStore";

const HIGHLIGHTS = [
  {
    icon: <NfcIcon />,
    title: "NFC payments",
    text: "Card recharge, balance check, and wallet billing at the counter.",
  },
  {
    icon: <PointOfSale />,
    title: "Fast POS",
    text: "Grocery, fruit & veg, restaurant KOT — one admin for every store type.",
  },
  {
    icon: <Storefront />,
    title: "Multi-branch",
    text: "Branches, sales points, catalog, inventory, and reports in one place.",
  },
];

/** Public PayTool home — marketing landing with Work login. */
const LandingPage: React.FC = () => {
  const { isAuthenticated } = useAuthStore();
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: `linear-gradient(180deg, ${BRAND.colors.orange50} 0%, #fff 42%, ${alpha(BRAND.colors.navy, 0.04)} 100%)`,
      }}
    >
      <Box
        component="header"
        sx={{
          px: { xs: 2, md: 4 },
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            component="img"
            src={BRAND.logo.md}
            alt={BRAND.name}
            sx={{ width: 40, height: 40, objectFit: "contain" }}
          />
          <Typography
            variant="h6"
            fontWeight={800}
            sx={{ color: BRAND.colors.navy, letterSpacing: "-0.02em" }}
          >
            {BRAND.name}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: BRAND.colors.orange600, fontWeight: 700 }}
          >
            {BRAND.tagline}
          </Typography>
        </Stack>
        <Button
          component={RouterLink}
          to="/login"
          variant="contained"
          startIcon={<LoginOutlined />}
          sx={{
            bgcolor: BRAND.colors.orange600,
            "&:hover": { bgcolor: BRAND.colors.orange700 },
            fontWeight: 700,
            px: 2.5,
          }}
        >
          Work login
        </Button>
      </Box>

      <Container maxWidth="md" sx={{ flex: 1, py: { xs: 6, md: 10 } }}>
        <Stack spacing={3} alignItems="center" textAlign="center">
          <Box
            component="img"
            src={BRAND.logo.hero}
            alt=""
            sx={{ width: { xs: 96, md: 128 }, height: "auto", mb: 1 }}
          />
          <Typography
            variant="h3"
            fontWeight={800}
            sx={{
              color: BRAND.colors.navy,
              fontSize: { xs: "1.85rem", md: "2.75rem" },
              letterSpacing: "-0.03em",
              lineHeight: 1.15,
            }}
          >
            Run payments & counters with {BRAND.name}
          </Typography>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ color: BRAND.colors.orange600, letterSpacing: "0.02em" }}
          >
            {BRAND.tagline}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 520, fontSize: { xs: "1rem", md: "1.1rem" } }}
          >
            Admin portal for NFC wallets, retail billing, and restaurant sales —
            built for busy store teams.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} pt={1}>
            <Button
              component={RouterLink}
              to="/login"
              variant="contained"
              size="large"
              startIcon={<LoginOutlined />}
              sx={{
                bgcolor: BRAND.colors.orange600,
                "&:hover": { bgcolor: BRAND.colors.orange700 },
                fontWeight: 700,
                px: 4,
                py: 1.25,
              }}
            >
              Work login
            </Button>
          </Stack>
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2.5}
          mt={{ xs: 6, md: 8 }}
        >
          {HIGHLIGHTS.map((item) => (
            <Box
              key={item.title}
              sx={{
                flex: 1,
                p: 2.5,
                borderRadius: 2,
                bgcolor: "#fff",
                border: "1px solid",
                borderColor: "divider",
                textAlign: "left",
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(BRAND.colors.orange500, 0.12),
                  color: BRAND.colors.orange600,
                  mb: 1.5,
                }}
              >
                {item.icon}
              </Box>
              <Typography fontWeight={700} sx={{ color: BRAND.colors.navy, mb: 0.5 }}>
                {item.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {item.text}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Container>

      <Box
        component="footer"
        sx={{
          py: 2,
          textAlign: "center",
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: alpha("#fff", 0.7),
        }}
      >
        <Typography variant="caption" color="text.secondary" display="block">
          {BRAND.copyright} · {BRAND.name} · Staff access via Work login
        </Typography>
        <PoweredByBrand sx={{ mt: 0.5 }} />
      </Box>
    </Box>
  );
};

export default LandingPage;
